import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import {
  buildPredicate,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import { ReactComponent as IconEdit } from "@assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "@assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconPlus } from "@assets/Icons/IconPlusNV.svg";
import {
  CarrierRow,
  carrierColumns,
  deleteDialogFor,
  shapeCarrierRows,
} from "./CarriersData";
import { makeCarrierFields } from "./carriersFieldSpec";
import { carriersAdminText } from "./carriersLabels";
import { describeCarrierError } from "./carrierForm";
import CarrierModal from "./CarrierModal";
import {
  CarrierResponse,
  deleteCarrier,
  fetchCarriers,
  updateCarrier,
} from "./request/request";

const text = carriersAdminText;
const deleteText = carriersAdminText.delete;

const actionButtonClass =
  "flex items-center justify-center w-10 h-10 rounded border border-semantic-border-secondary text-semantic-text-secondary hover:bg-semantic-state-active";
const deleteButtonClass =
  "flex items-center justify-center w-10 h-10 p-2 rounded border border-semantic-border-secondary bg-semantic-bg-danger-secondary text-semantic-text-danger hover:opacity-90";

/** A carrier that can't be deleted because services still use it. */
interface InUseState {
  row: CarrierRow;
  serviceCount: number;
}

/** The modal is closed, creating, or editing one carrier. */
type ModalState = { carrier: CarrierResponse | null } | null;

/**
 * Admin Center → Kontrol Edge → Carriers (CTX-786): the catalog the Carrier
 * Bridge form picks from. Adds, edits, deactivates and deletes carriers; a
 * carrier still used by services cannot be deleted, so the refusal offers to
 * deactivate it instead. An empty catalog keeps the table on screen so the
 * first carrier can be added.
 */
const CarriersPage: React.FC = () => {
  const api = useAxiosAuth0();
  const [rows, setRows] = useState<CarrierRow[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

  const [modal, setModal] = useState<ModalState>(null);
  const [rowToDelete, setRowToDelete] = useState<CarrierRow | null>(null);
  const [inUse, setInUse] = useState<InUseState | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const fields = useMemo(() => makeCarrierFields(), []);
  const suggestedKeys = useMemo(() => fields.map((f) => f.label), [fields]);

  const normalized = useMemo(
    () =>
      normalizeFilters<CarrierRow>(activeFilters, fields, {
        defaultStringToContains: true,
        quotedStringsExact: true,
      }),
    [activeFilters, fields]
  );
  const predicate = useMemo(
    () => buildPredicate<CarrierRow>(normalized, fields),
    [normalized, fields]
  );
  const filteredRows = useMemo(() => rows.filter(predicate), [rows, predicate]);

  const loadList = useCallback(async () => {
    setIsFetching(true);
    setFetchError(null);
    try {
      const { status, data } = await fetchCarriers(api);
      if (status >= 500 || data.success === false) {
        setFetchError(data.message || "Unknown server error");
        setRows([]);
        return;
      }
      setRows(Array.isArray(data.data) ? shapeCarrierRows(data.data) : []);
    } catch (err) {
      console.error("Error fetching carriers", err);
      const message =
        err instanceof AxiosError
          ? err.response?.data?.message ?? err.message
          : "Network error";
      setFetchError(message);
      setRows([]);
    } finally {
      setIsFetching(false);
      setHasFetched(true);
    }
  }, [api]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const startDelete = useCallback((row: CarrierRow) => {
    if (deleteDialogFor(row) === "in-use") {
      setInUse({ row, serviceCount: row.serviceCount });
    } else {
      setRowToDelete(row);
    }
  }, []);

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    const row = rowToDelete;
    setRowToDelete(null);
    try {
      await deleteCarrier(api, row.id);
      await loadList();
    } catch (err) {
      console.error("Error deleting carrier", err);
      const described = describeCarrierError(err);
      if (described.code === "CARRIER_IN_USE") {
        setInUse({ row, serviceCount: described.serviceCount ?? row.serviceCount });
      } else {
        setActionError(described.message);
      }
    }
  };

  const deactivate = async () => {
    if (!inUse) return;
    const { row } = inUse;
    setInUse(null);
    try {
      await updateCarrier(api, row.id, { available: false });
      await loadList();
    } catch (err) {
      console.error("Error deactivating carrier", err);
      setActionError(describeCarrierError(err).message);
    }
  };

  const columns = useMemo<GenericTableColumn<CarrierRow>[]>(
    () => [
      ...carrierColumns,
      {
        header: text.columns.actions,
        id: "actions",
        renderCell: (row) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={actionButtonClass}
              onClick={() => setModal({ carrier: row.raw })}
              title="Edit"
              aria-label={`Edit ${row.name}`}
            >
              <IconEdit className="w-9 h-9" />
            </button>
            <button
              type="button"
              className={deleteButtonClass}
              onClick={() => startDelete(row)}
              title="Delete"
              aria-label={`Delete ${row.name}`}
            >
              <IconDelete className="w-5 h-5" />
            </button>
          </div>
        ),
      },
    ],
    [startDelete]
  );

  const showInitialSkeleton = !hasFetched || (isFetching && rows.length === 0);

  if (fetchError) return <ErrorFetchingItems text={fetchError} />;

  return (
    <GenericCard isCollapsible={false}>
      <div className="mb-4 p-5 bg-semantic-bg-primary">
        <div className="flex items-end justify-between">
          <div className="w-[520px] my-5">
            <GenericSearchbar
              placeholder={text.searchPlaceholder}
              suggestedKeys={suggestedKeys}
              onFiltersChange={setActiveFilters}
              liveFiltering
              debounceMs={200}
            />
          </div>
          <div className="mb-10">
            <GenericButton
              label={
                <div className="flex items-center gap-2">
                  <IconPlus className="w-5 h-5" />
                  <span>{text.addButton}</span>
                </div>
              }
              variant="brand"
              size="md"
              onClick={() => setModal({ carrier: null })}
            />
          </div>
        </div>
      </div>

      <div className="px-5 mb-5">
        {/* The table's own slot in skeleton. The overlay this replaces covered
            the rail and the views bar for the whole wait (CTX-1125). */}
        {showInitialSkeleton ? (
          <TableSkeleton columns={columns.length} rows={5} />
        ) : (
          <GenericTable
            columns={columns}
            data={filteredRows}
            initialRowsPerPage={10}
            rowsPerPageOptions={[10, 20, 30]}
            tableClassName="bg-semantic-bg-primary"
            width="100%"
          />
        )}
      </div>

      <CarrierModal
        isOpen={modal !== null}
        carrier={modal?.carrier ?? null}
        api={api}
        onClose={() => setModal(null)}
        onSaved={loadList}
      />

      <GenericAlert
        isOpen={rowToDelete !== null}
        onClose={() => setRowToDelete(null)}
        onConfirm={confirmDelete}
        title={deleteText.title(rowToDelete?.name ?? "")}
        description={deleteText.description}
        cancelLabel={deleteText.cancel}
        confirmLabel={deleteText.confirm}
        variant="error"
      />

      <GenericAlert
        isOpen={inUse !== null}
        onClose={() => setInUse(null)}
        onConfirm={deactivate}
        title={deleteText.inUseTitle(inUse?.row.name ?? "")}
        description={deleteText.inUseDescription(inUse?.serviceCount ?? 0)}
        cancelLabel={deleteText.cancel}
        confirmLabel={deleteText.deactivate}
        variant="warning"
      />

      <GenericAlert
        isOpen={actionError !== null}
        onClose={() => setActionError(null)}
        onConfirm={() => setActionError(null)}
        title={deleteText.failedTitle}
        description={actionError ?? ""}
        confirmLabel={deleteText.failedConfirm}
        variant="warning"
        hideCancel
      />
    </GenericCard>
  );
};

export default CarriersPage;
